import { useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { Link, Navigate, useParams } from '@tanstack/react-router'
import {
  AUDIT_CYCLE_STATUS_LABELS,
  FRAMEWORK_ENROLLMENT_FLAGS,
} from '@deepguide-ai/types'
import { Badge } from '@deepguide-ai/ui/badge'
import { Button } from '@deepguide-ai/ui/button'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@deepguide-ai/ui/select'
import { Skeleton } from '@deepguide-ai/ui/skeleton'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@deepguide-ai/ui/tabs'
import {
  ArrowUpRight,
  Calendar,
  Mail,
  Shield,
  MessageSquareMore,
} from 'lucide-react'
import {
  frameworkOptions,
  getFrameworkDisplayName,
  getFrameworkLogoUrl,
} from '@/lib/compliance-framework'
import { trpc } from '@/lib/trpc'
import { cn } from '@/lib/utils'
import { useFeatureFlag } from '@/hooks/useFeatureFlag'
import { useWorkspacePermissions } from '@/hooks/useWorkspacePermissions'
import ScreenataPageHeader from '@/components/compliance/ScreenataPageHeader'
import { ScreenataPageLayout } from '@/components/compliance/ScreenataPageLayout'
import { SegBar } from '@/components/compliance/SegBar'
import { AuditorApiGuide } from './AuditorApiGuide'
import { AuditorApiTokensCard } from './AuditorApiTokensCard'
import { CreateAuditCycleDialog } from './CreateAuditCycleDialog'
import {
  formatAuditCycleType,
  formatAuditCyclePeriod,
} from './auditCycleLabels'
import { useAuditCycleOverview } from './useAuditCycleOverview'

export function AuditorCenterPage() {
  const { workspaceId } = useParams({ strict: false })
  const { isAuditor } = useWorkspacePermissions(workspaceId)

  if (!isAuditor) {
    return (
      <div className='flex h-full items-center justify-center'>
        <p className='text-text-subtle text-[14px]'>
          The Auditor portal is only available to invited auditors.
        </p>
      </div>
    )
  }

  return <Navigate to='/auditor-center' replace />
}

export function AuditorApiAccessPage() {
  const { workspaceId, auditCycleId } = useParams({ strict: false })
  const { isAuditor, isAdmin, isOwner } = useWorkspacePermissions(workspaceId)
  const flag = useFeatureFlag('auditor-pull-api')

  const isAdminPreview =
    Boolean(auditCycleId) && !isAuditor && (isAdmin || isOwner)

  if (!isAuditor && !isAdminPreview) return <AuditorCenterPage />

  return (
    <div className='h-full overflow-y-auto px-6 py-8'>
      <h1 className='text-text-secondary mb-6 text-[22px] font-bold'>
        API access
      </h1>
      {flag.enabled ? (
        <AuditorApiGuide />
      ) : (
        <p className='text-muted-foreground text-sm'>
          API access is disabled for this workspace.
        </p>
      )}
      {isAdminPreview ? (
        <p className='text-muted-foreground mt-6 text-sm'>
          Preview only. API tokens belong to each auditor’s workspace seat.
          Auditors manage their own tokens when signed in.
        </p>
      ) : (
        <AuditorApiTokensCard key={workspaceId} />
      )}
    </div>
  )
}

export function AuditsAdminPage() {
  const { workspaceId } = useParams({ strict: false })
  const { isAdmin, isOwner } = useWorkspacePermissions(workspaceId)
  const isAdminUser = isAdmin || isOwner

  if (!isAdminUser) {
    return (
      <div className='flex h-full items-center justify-center'>
        <p className='text-text-subtle text-[14px]'>
          You don&apos;t have access to Audits.
        </p>
      </div>
    )
  }

  return <AuditorAdminView workspaceId={workspaceId!} />
}

export function AuditorMyDashboard() {
  const [cycleTab, setCycleTab] = useState('active')
  const [workspaceFilter, setWorkspaceFilter] = useState('all')
  const {
    data: cycles,
    isLoading,
    isError,
    refetch,
  } = useQuery(trpc.auditor.myAudits.queryOptions())
  const workspaces = Array.from(
    new Map(
      cycles?.map((cycle) => [
        cycle.workspaceId,
        cycle.organizationName && cycle.organizationName !== cycle.workspaceName
          ? `${cycle.organizationName} · ${cycle.workspaceName}`
          : cycle.workspaceName,
      ])
    ).entries()
  )
  const workspaceCycles = cycles?.filter(
    (cycle) =>
      workspaceFilter === 'all' || cycle.workspaceId === workspaceFilter
  )
  const activeCycles =
    workspaceCycles?.filter((cycle) => cycle.status !== 'completed') ?? []
  const inactiveCycles =
    workspaceCycles?.filter((cycle) => cycle.status === 'completed') ?? []
  const visibleCycles = cycleTab === 'active' ? activeCycles : inactiveCycles

  return (
    <div className='h-full overflow-y-auto px-6 py-8'>
      <div className='mb-6 flex flex-wrap items-end justify-between gap-4'>
        <div>
          <h1 className='text-text-secondary text-[22px] font-bold'>Audits</h1>
          <p className='text-text-subtle mt-1 text-[14px]'>
            Your assigned audits across workspaces.
          </p>
        </div>
        <Select value={workspaceFilter} onValueChange={setWorkspaceFilter}>
          <SelectTrigger
            aria-label='Filter audits by workspace'
            className='w-[220px]'
          >
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value='all'>All workspaces</SelectItem>
            {workspaces.map(([id, name]) => (
              <SelectItem key={id} value={id}>
                {name}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      <Tabs value={cycleTab} onValueChange={setCycleTab}>
        <TabsList aria-label='Audit cycle activity' className='mb-4'>
          <TabsTrigger value='active'>
            Active{cycles ? ` (${activeCycles.length})` : ''}
          </TabsTrigger>
          <TabsTrigger value='inactive'>
            Inactive{cycles ? ` (${inactiveCycles.length})` : ''}
          </TabsTrigger>
        </TabsList>
        <TabsContent value={cycleTab}>
          {isLoading ? (
            <div className='text-text-subtle text-[14px]'>Loading…</div>
          ) : isError ? (
            <div className='flex items-center gap-3'>
              <p className='text-text-subtle text-[14px]'>
                Unable to load audits.
              </p>
              <Button
                variant='outline'
                size='sm'
                onClick={() => void refetch()}
              >
                Retry
              </Button>
            </div>
          ) : !cycles?.length ? (
            <EmptyCard
              icon={Shield}
              title='No audits assigned yet.'
              description='A workspace admin needs to grant you access to an audit before you can review controls, evidence, and policies here.'
            />
          ) : !visibleCycles.length ? (
            <EmptyCard
              icon={Shield}
              title={
                cycleTab === 'active'
                  ? 'No active audits.'
                  : 'No inactive audits.'
              }
              description={
                cycleTab === 'active'
                  ? 'Completed audits are available in the Inactive tab.'
                  : 'Completed audits will appear here and remain available to read.'
              }
            />
          ) : (
            <div className='grid gap-4 md:grid-cols-2 xl:grid-cols-3'>
              {visibleCycles.map((cycle) => {
                const logo = getFrameworkLogoUrl(cycle.framework)
                return (
                  <Link
                    key={cycle.id}
                    to='/workspaces/$workspaceId/auditor-center/$auditCycleId/dashboard'
                    params={{
                      workspaceId: cycle.workspaceId,
                      auditCycleId: cycle.id,
                    }}
                    preload={false}
                    className='group border-border bg-card hover:border-ring focus-visible:ring-ring flex min-w-0 flex-col overflow-hidden rounded-sm border transition-colors focus-visible:ring-2 focus-visible:outline-none'
                  >
                    <div className='flex flex-1 flex-col p-5'>
                      <div className='mb-4 flex items-center justify-between gap-3'>
                        {logo ? (
                          <img
                            src={logo}
                            alt={`${getFrameworkDisplayName(cycle.framework)} logo`}
                            className='size-12 shrink-0 object-contain'
                          />
                        ) : (
                          <Shield className='text-text-subtle size-12 shrink-0' />
                        )}
                        <CycleStatusBadge status={cycle.status} />
                      </div>
                      <div className='min-w-0 flex-1'>
                        <h3 className='text-text-secondary group-hover:text-cobalt-strong text-[16px] leading-snug font-semibold'>
                          {cycle.organizationName
                            ? `${cycle.organizationName} · `
                            : ''}
                          {cycle.label}
                        </h3>
                        <p className='text-text-subtle mt-1 text-[13px]'>
                          {[
                            formatAuditCycleType(
                              cycle.auditType,
                              cycle.framework
                            ),
                            formatAuditCyclePeriod(
                              cycle.periodStart,
                              cycle.periodEnd
                            ),
                          ]
                            .filter(Boolean)
                            .join(' · ')}
                        </p>
                        {cycle.framework && (
                          <Badge variant='outline' className='mt-3 text-[11px]'>
                            {getFrameworkDisplayName(cycle.framework)}
                          </Badge>
                        )}
                      </div>
                    </div>
                    <div className='border-border text-cobalt-strong flex items-center justify-between border-t px-5 py-3 text-xs'>
                      <span>Open audit</span>
                      <ArrowUpRight className='size-3.5' />
                    </div>
                  </Link>
                )
              })}
            </div>
          )}
        </TabsContent>
      </Tabs>
    </div>
  )
}

function AuditorAdminView({ workspaceId }: { workspaceId: string }) {
  const [createCycleOpen, setCreateCycleOpen] = useState(false)

  // The Audits hub spans every enrolled framework — omit `framework` to list
  // cycles across all of them; each card shows its own framework badge.
  const cyclesQuery = useQuery(trpc.auditor.listAuditCycles.queryOptions())
  const { data: auditCycles, isLoading: isLoadingCycles } = cyclesQuery
  const enrollmentQuery = useQuery(
    trpc.complianceProgram.listEnrolledFrameworks.queryOptions()
  )
  const enabledFrameworks = new Set(
    (enrollmentQuery.data?.frameworks ?? []).map((framework) => framework.key)
  )
  // Contact-only catalog: show supported frameworks even when self-service enrollment is gated.
  const availableFrameworks = frameworkOptions(
    Object.fromEntries(
      Object.values(FRAMEWORK_ENROLLMENT_FLAGS).map((flag) => [flag, true])
    )
  ).filter(
    (framework) =>
      framework.value !== 'OTHER' && !enabledFrameworks.has(framework.value)
  )

  const headerActions = (
    <div className='flex items-center gap-2'>
      <Button
        size='sm'
        onClick={() => setCreateCycleOpen(true)}
        className='h-8 rounded-sm px-3 text-[12px]'
      >
        <Calendar className='h-3.5 w-3.5' />
        New Audit
      </Button>
    </div>
  )

  return (
    <ScreenataPageLayout
      title='Audits'
      headerActions={headerActions}
      width='full'
      scroll
    >
      <div>
        <ScreenataPageHeader
          className='mb-6'
          title='Audits'
          description='Manage audits and collaborate with invited auditors. Open an audit to review controls, evidence, policies, and threads.'
        />

        {isLoadingCycles ? (
          <AuditCycleListSkeleton />
        ) : cyclesQuery.isError ? (
          <div className='border-border bg-card rounded-sm border p-5'>
            <p className='text-text-subtle text-sm'>
              Could not load your audit cycles.
            </p>
            <Button
              variant='outline'
              size='sm'
              className='mt-3'
              onClick={() => void cyclesQuery.refetch()}
            >
              Retry
            </Button>
          </div>
        ) : !auditCycles?.length ? (
          <EmptyCard
            icon={Calendar}
            title='No audits yet'
            description='Create an audit to start sharing controls and evidence with auditors.'
          />
        ) : (
          <div className='space-y-8'>
            {groupCyclesByYear(auditCycles).map((group) => (
              <div key={group.key}>
                <h3 className='text-text-placeholder mb-2 px-1 text-[11px] font-semibold tracking-[0.08em] uppercase'>
                  {group.label}
                </h3>
                <div className='grid gap-4 md:grid-cols-2 xl:grid-cols-3'>
                  {group.cycles.map((cycle: any) => (
                    <AuditCycleRow
                      key={cycle.id}
                      cycle={cycle}
                      workspaceId={workspaceId}
                    />
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}

        {enrollmentQuery.isLoading ? (
          <Skeleton className='mt-8 h-48 w-full rounded-sm' />
        ) : enrollmentQuery.isError ? (
          <div className='border-border bg-card mt-8 rounded-sm border p-5'>
            <p className='text-text-subtle text-sm'>
              Could not check which additional frameworks are available.
            </p>
            <Button
              variant='outline'
              size='sm'
              className='mt-3'
              onClick={() => void enrollmentQuery.refetch()}
            >
              Retry
            </Button>
          </div>
        ) : (
          availableFrameworks.length > 0 && (
            <section aria-labelledby='available-frameworks' className='mt-10'>
              <h2
                id='available-frameworks'
                className='text-text-secondary text-[18px] font-semibold'
              >
                Available frameworks
              </h2>
              <p className='text-text-subtle mt-1 text-sm'>
                Expand your compliance program. These frameworks are supported
                by Screenata and aren’t enabled in this workspace.
              </p>
              <div className='mt-4 grid gap-4 md:grid-cols-2 xl:grid-cols-3'>
                {availableFrameworks.map((framework) => (
                  <article
                    key={framework.value}
                    className='border-border bg-card flex flex-col rounded-sm border p-5'
                  >
                    <div className='flex items-center gap-3'>
                      <img
                        src={getFrameworkLogoUrl(framework.value)!}
                        alt={`${framework.label} logo`}
                        className='size-12 shrink-0 object-contain'
                      />
                      <div>
                        <h3 className='text-text-secondary text-[15px] font-semibold'>
                          {framework.label}
                        </h3>
                        <p className='text-text-subtle mt-1 text-xs'>
                          Not enabled
                        </p>
                      </div>
                    </div>
                    <p className='text-text-subtle mt-4 flex-1 text-[13px] leading-relaxed'>
                      {framework.description}
                    </p>
                    <Button
                      asChild
                      variant='outline'
                      size='sm'
                      className='mt-5 self-start'
                    >
                      <a
                        href={`mailto:support@screenata.com?subject=${encodeURIComponent(`Enable ${framework.label} for my workspace`)}`}
                        aria-label={`Contact Us about ${framework.label}`}
                      >
                        <Mail className='size-3.5' /> Contact Us
                      </a>
                    </Button>
                  </article>
                ))}
              </div>
            </section>
          )
        )}

        <CreateAuditCycleDialog
          open={createCycleOpen}
          onOpenChange={setCreateCycleOpen}
        />
      </div>
    </ScreenataPageLayout>
  )
}

// A cycle created via RFC-160 before its window is scheduled can carry an
// unset date that serializes to the Unix epoch (1/1/1970) rather than null.
// Treat null AND any pre-2000 date as "not set" so it never renders as a bogus
// 1970 date or buckets into a "1970" year header.
function isUnsetCycleDate(value: string | Date | null | undefined): boolean {
  if (!value) return true
  const time = new Date(value).getTime()
  return Number.isNaN(time) || time < Date.parse('2000-01-01')
}

// Group cycles into year buckets keyed off `periodEnd` (the date the audit
// period closes). RFC-160 allows a cycle to exist before its dates are set,
// so undated cycles collect in an "Unscheduled" bucket surfaced first — those
// are the freshly-created ones still needing dates. Years sort descending.
function groupCyclesByYear(
  cycles: any[]
): { key: string; label: string; cycles: any[] }[] {
  const buckets = new Map<string, any[]>()
  for (const cycle of cycles) {
    const key = !isUnsetCycleDate(cycle.periodEnd)
      ? String(new Date(cycle.periodEnd).getFullYear())
      : 'unscheduled'
    const bucket = buckets.get(key)
    if (bucket) bucket.push(cycle)
    else buckets.set(key, [cycle])
  }

  const years = [...buckets.keys()]
    .filter((key) => key !== 'unscheduled')
    .sort((a, b) => Number(b) - Number(a))

  const groups: { key: string; label: string; cycles: any[] }[] = []
  const unscheduled = buckets.get('unscheduled')
  if (unscheduled) {
    groups.push({
      key: 'unscheduled',
      label: 'Unscheduled',
      cycles: unscheduled,
    })
  }
  for (const year of years) {
    groups.push({ key: year, label: year, cycles: buckets.get(year)! })
  }
  return groups
}

function AuditCycleListSkeleton() {
  return (
    <div className='grid gap-4 md:grid-cols-2 xl:grid-cols-3'>
      {Array.from({ length: 3 }, (_, index) => (
        <Skeleton key={index} className='h-[340px] rounded-sm' />
      ))}
    </div>
  )
}

export function AuditCycleRow({
  cycle,
  workspaceId,
  variant = 'card',
}: {
  cycle: any
  workspaceId: string
  variant?: 'card' | 'row'
}) {
  const { query, model, evidence, approved, applicable } =
    useAuditCycleOverview(cycle.id)
  const isLoading =
    query.isPending || (query.hasNextPage && query.isFetchingNextPage)
  const isError =
    query.isError || (query.hasNextPage && !query.isFetchingNextPage)
  const inReview = model.controls.filter(
    (control) => control.approvalStage === 'NEEDS_APPROVAL'
  ).length
  const requestsOptions = trpc.auditor.listOpenRequests.queryOptions({
    auditCycleId: cycle.id,
  })
  const requestsQuery = useQuery({
    ...requestsOptions,
    queryKey: [...requestsOptions.queryKey, { workspaceId }],
    enabled: variant === 'card',
    refetchInterval: 15_000,
  })
  const progress = [
    {
      label: 'Controls approved',
      count: approved,
      total: applicable,
      isLoading,
      isError,
      detail: undefined,
    },
    ...(variant === 'card'
      ? [
          {
            label: 'Tests with evidence',
            count: evidence.withEvidence,
            total: evidence.total,
            isLoading,
            isError,
            detail: `Submitted ${evidence.submitted}/${evidence.manual} · Automated passing ${evidence.passing}/${evidence.automated}`,
          },
        ]
      : []),
  ]
  const controlStatuses = [
    {
      label: 'Approved',
      value: approved,
      className: 'bg-status-green',
      textClassName: 'text-status-green',
    },
    {
      label: 'In review',
      value: inReview,
      className: 'bg-status-blue',
      textClassName: 'text-status-blue-strong',
    },
    {
      label: 'Needs work',
      value: applicable - approved - inReview,
      className: 'bg-status-amber-emphasis',
      textClassName: 'text-status-amber-emphasis',
    },
    {
      label: 'N/A',
      value: model.controls.length - applicable,
      className: 'bg-border-divider',
      textClassName: 'text-muted-foreground',
    },
  ]
  const hasDates =
    !isUnsetCycleDate(cycle.periodStart) || !isUnsetCycleDate(cycle.periodEnd)
  const logo = getFrameworkLogoUrl(cycle.framework)

  return (
    <Link
      to='/workspaces/$workspaceId/audits/$auditCycleId'
      params={{ workspaceId, auditCycleId: cycle.id }}
      preload={false}
      className={cn(
        'group focus-visible:ring-ring flex min-w-0 flex-col transition-colors focus-visible:ring-2 focus-visible:outline-none',
        variant === 'row'
          ? 'hover:bg-muted/40'
          : 'border-border bg-card hover:border-ring overflow-hidden rounded-sm border'
      )}
    >
      <div
        className={cn(
          'flex flex-1 flex-col',
          variant === 'row' ? 'py-4' : 'p-5'
        )}
      >
        <div className='flex items-start gap-3'>
          {logo ? (
            <img
              src={logo}
              alt={`${getFrameworkDisplayName(cycle.framework)} logo`}
              className={cn(
                'shrink-0 object-contain',
                variant === 'row' ? 'size-9' : 'size-12'
              )}
            />
          ) : (
            <Shield
              className={cn(
                'text-text-subtle shrink-0',
                variant === 'row' ? 'size-9' : 'size-12'
              )}
            />
          )}
          <div className='min-w-0 flex-1'>
            <div className='mb-1.5 flex flex-wrap items-center justify-between gap-2'>
              <span className='text-text-subtle text-xs'>
                {getFrameworkDisplayName(cycle.framework)} ·{' '}
                {formatAuditCycleType(cycle.auditType, cycle.framework)}
              </span>
              <CycleStatusBadge status={cycle.status} />
            </div>
            <h3 className='text-text-secondary group-hover:text-cobalt-strong text-[15px] leading-snug font-semibold'>
              {cycle.label}
            </h3>
          </div>
        </div>

        <div className={cn('space-y-4', variant === 'row' ? 'my-3' : 'my-5')}>
          {progress.map((metric) => (
            <div key={metric.label} className='space-y-2'>
              <div className='flex items-center justify-between gap-3 text-xs'>
                <span className='text-text-body'>{metric.label}</span>
                {metric.isLoading ? (
                  <Skeleton className='h-4 w-16 rounded-sm' />
                ) : (
                  <span className='text-text-secondary font-medium tabular-nums'>
                    {metric.isError || metric.total == null
                      ? 'Unavailable'
                      : metric.total === 0
                        ? 'No applicable items'
                        : `${Math.round(((metric.count ?? 0) / metric.total) * 100)}%`}
                  </span>
                )}
              </div>
              {metric.detail && !metric.isLoading && !metric.isError && (
                <p className='text-text-subtle text-xs'>{metric.detail}</p>
              )}
              {metric.isLoading ? (
                <Skeleton className='h-1.5 w-full rounded-full' />
              ) : (
                <div aria-hidden='true'>
                  <SegBar
                    segments={
                      metric.isError
                        ? []
                        : metric.label === 'Controls approved'
                          ? controlStatuses
                          : [
                              {
                                value: metric.count,
                                className: 'bg-status-green',
                              },
                              {
                                value: Math.max(0, metric.total - metric.count),
                                className: 'bg-border-divider',
                              },
                            ]
                    }
                  />
                </div>
              )}
              {metric.isLoading && metric.label === 'Controls approved' ? (
                <div aria-hidden='true' className='grid grid-cols-4 gap-2'>
                  {controlStatuses.map((status) => (
                    <Skeleton key={status.label} className='h-9 w-full' />
                  ))}
                </div>
              ) : !metric.isError && metric.label === 'Controls approved' ? (
                <dl
                  aria-label='Control statuses'
                  className='grid grid-cols-4 gap-2 pt-1'
                >
                  {controlStatuses.map((status) => (
                    <div
                      key={status.label}
                      className='flex min-w-0 flex-col items-center gap-1 text-center'
                    >
                      <dt className='text-text-subtle order-2 text-[10px] leading-tight'>
                        {status.label}
                      </dt>
                      <dd
                        className={cn(
                          'text-sm leading-none font-semibold tabular-nums',
                          status.textClassName
                        )}
                      >
                        {status.value}
                      </dd>
                    </div>
                  ))}
                </dl>
              ) : null}
            </div>
          ))}
        </div>

        <dl
          className={cn(
            'mt-auto grid gap-3 text-xs',
            variant === 'card' && 'grid-cols-2'
          )}
        >
          <div className='grid min-w-0 grid-cols-[auto_minmax(0,1fr)] items-center gap-x-2'>
            <dt className='text-text-subtle inline-flex shrink-0 items-center gap-1.5'>
              <Calendar aria-hidden='true' className='size-3.5 shrink-0' />{' '}
              Period
            </dt>
            <dd className='text-text-body min-w-0'>
              {hasDates
                ? `${isUnsetCycleDate(cycle.periodStart) ? '—' : new Date(cycle.periodStart).toLocaleDateString()} – ${isUnsetCycleDate(cycle.periodEnd) ? '—' : new Date(cycle.periodEnd).toLocaleDateString()}`
                : 'Dates not set'}
            </dd>
          </div>
          {variant === 'card' && (
            <div className='flex min-w-0 flex-wrap items-baseline gap-x-2 gap-y-1'>
              <dt className='text-text-subtle inline-flex shrink-0 items-center gap-1.5'>
                <MessageSquareMore aria-hidden='true' className='size-3.5' />{' '}
                Open Requests
              </dt>
              <dd className='text-text-body min-w-0'>
                {requestsQuery.isPending
                  ? 'Loading…'
                  : requestsQuery.isError
                    ? 'Unavailable'
                    : (requestsQuery.data?.total ?? 'Unavailable')}
              </dd>
            </div>
          )}
          {!isUnsetCycleDate(cycle.targetAuditDate) && (
            <div className='flex min-w-0 flex-wrap items-baseline gap-x-2 gap-y-1'>
              <dt className='text-text-subtle'>Target date</dt>
              <dd className='text-text-body'>
                {new Date(cycle.targetAuditDate).toLocaleDateString()}
              </dd>
            </div>
          )}
        </dl>
      </div>
    </Link>
  )
}

// Engagement-lifecycle status pill. Frameworks cards lead with a coverage
// metric; audit-cycle cards lead with where the engagement is in its
// lifecycle — this is the signal that keeps the two card types visually
// distinct so they don't read as duplicates of the same list.
const CYCLE_STATUS_STYLE: Record<string, { label: string; className: string }> =
  {
    planning: {
      label: AUDIT_CYCLE_STATUS_LABELS.planning,
      className: 'bg-muted text-muted-foreground',
    },
    active: {
      label: AUDIT_CYCLE_STATUS_LABELS.active,
      className: 'bg-primary-subtle text-status-blue-strong',
    },
    'audit-ready': {
      label: AUDIT_CYCLE_STATUS_LABELS['audit-ready'],
      className: 'bg-status-green-surface-subtle text-status-green',
    },
    'in-audit': {
      label: AUDIT_CYCLE_STATUS_LABELS['in-audit'],
      className: 'bg-status-orange-bg text-status-orange',
    },
    completed: {
      label: AUDIT_CYCLE_STATUS_LABELS.completed,
      className: 'bg-status-green-surface-subtle text-status-green',
    },
  }

export function CycleStatusBadge({ status }: { status?: string | null }) {
  const style = CYCLE_STATUS_STYLE[status ?? 'planning'] ?? {
    label: status ?? 'Planning',
    className: 'bg-muted text-muted-foreground',
  }
  return (
    <span
      className={cn(
        'inline-flex items-center rounded-full px-2 py-0.5 text-[10px] font-medium tracking-[0.04em] uppercase',
        style.className
      )}
    >
      {style.label}
    </span>
  )
}

function EmptyCard({
  icon: Icon,
  title,
  description,
  className,
}: {
  icon: any
  title: string
  description: string
  className?: string
}) {
  return (
    <div
      className={`border-border-muted rounded-sm border p-8 text-center ${className ?? ''}`}
    >
      <Icon className='text-text-placeholder mx-auto mb-3 h-5 w-5' />
      <p className='text-text-secondary text-[13px] font-medium'>{title}</p>
      <p className='text-text-subtle mt-1 text-[13px]'>{description}</p>
    </div>
  )
}
